import { useCallback, useEffect, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useSnackbar } from 'baseui/snackbar'
import { useTranslation } from 'react-i18next'

import { selectFeedbackStoreData } from './feedbackSelectors'
import { setSearch, setStart, setEnd, setFeedbackType, setVersion, setPage } from './feedbackSlice'
import { feedbackActions } from './feedbackSagas'

const useFeedback = () => {
  const dispatch = useDispatch()
  const { enqueue } = useSnackbar()
  const { t } = useTranslation('devToolsNew')

  const {
    search, start, end, feedbackType, version,
    page, perPage, items, total, isLoading, error,
    isExporting, exportError,
  } = useSelector(selectFeedbackStoreData)

  const fetchList = useCallback(
    nextPage => {
      dispatch({
        type: feedbackActions.FETCH_LIST,
        t,
        payload: { search, start, end, feedbackType, version, page: nextPage || 1, perPage },
      })
    },
    [dispatch, t, search, start, end, feedbackType, version, perPage],
  )

  useEffect(() => {
    fetchList(1)
  }, [])

  const onSearch = useCallback(() => {
    dispatch(setPage(1))
    fetchList(1)
  }, [dispatch, fetchList])

  const totalPages = useMemo(() => Math.max(1, Math.ceil(total / (perPage || 1))), [total, perPage])
  const canGoPrevious = page > 1 && !isLoading
  const canGoNext = page < totalPages && !isLoading

  const goToPage = useCallback(
    nextPage => {
      dispatch(setPage(nextPage))
      fetchList(nextPage)
    },
    [dispatch, fetchList],
  )

  const goPreviousPage = useCallback(() => {
    if (!canGoPrevious) return
    goToPage(page - 1)
  }, [canGoPrevious, goToPage, page])

  const goNextPage = useCallback(() => {
    if (!canGoNext) return
    goToPage(page + 1)
  }, [canGoNext, goToPage, page])

  const onExportCsv = useCallback(() => {
    dispatch({
      type: feedbackActions.EXPORT_CSV,
      enqueue,
      t,
      payload: { search, start, end, feedbackType, version },
    })
  }, [dispatch, enqueue, t, search, start, end, feedbackType, version])

  return {
    filters: {
      search,
      start,
      end,
      feedbackType,
      version,
      isLoading,
      onSearchChange: value => dispatch(setSearch(value)),
      onStartChange: value => dispatch(setStart(value)),
      onEndChange: value => dispatch(setEnd(value)),
      onFeedbackTypeChange: value => dispatch(setFeedbackType(value)),
      onVersionChange: value => dispatch(setVersion(value)),
      onSearch,
    },
    table: {
      rows: Array.isArray(items) ? items : [],
      isLoading,
      error,
      pagination: {
        canGoPrevious,
        canGoNext,
        goPreviousPage,
        goNextPage,
      },
    },
    exportCsv: {
      isExporting,
      exportError,
      onExportCsv,
    },
    t,
  }
}

export default useFeedback
